import React from 'react'
import type { TableColumn, EditingContext } from '../../../../ui_configs/table_grid.ts'
import type { SortConfig, FilterConfig } from '../TableViewTypes'

interface ProcessColumnBaseParams<T> {
  column: TableColumn<T>
  columnIndex: number
  columnKey: string
  columns: TableColumn<T>[]
  columnWidths: Record<string, number>
  editingColumnWidths: Record<string, number>
  frozenColumns: (string | number)[]
  currentSortConfig: SortConfig | null
  currentFilters: FilterConfig
  handleResizeStart: (e: React.MouseEvent, columnKey: string) => void
  editingCell: string | null
  editingCellData: string
  enableCellEditing: boolean
  handleCellEdit: (recordId: string | number, columnKey: string, currentValue: any) => void
  handleCellSave: (recordId: string | number, columnKey: string) => void
  handleCellCancel: () => void
  handleCellDataChange: (value: any) => void
  handleFieldEdit: (columnKey: string, isEditing: boolean) => void
  renderStandardEditCell: (
    column: TableColumn<T>,
    item: T,
    value: any,
    onChange: (value: any) => void,
    onSave: () => void,
    onCancel: () => void
  ) => React.ReactNode
}

export function processColumnBase<T extends { id: string | number } = any>({
  column,
  columnIndex,
  columnKey,
  columns,
  columnWidths,
  editingColumnWidths,
  frozenColumns,
  currentSortConfig,
  currentFilters,
  handleResizeStart,
  editingCell,
  editingCellData,
  enableCellEditing,
  handleCellEdit,
  handleCellSave,
  handleCellCancel,
  handleCellDataChange,
  handleFieldEdit,
  renderStandardEditCell
}: ProcessColumnBaseParams<T>): TableColumn<T> {
  const shouldFreeze = frozenColumns.includes(columnKey) || frozenColumns.includes(columnIndex)

  const baseWidth = columnWidths[columnKey] || column.width || 150
  const effectiveWidth = editingColumnWidths[columnKey] || baseWidth

  let columnWithSort: TableColumn<T> = {
    ...column,
    width: effectiveWidth,
    fixed: shouldFreeze ? 'left' : undefined,
    onHeaderCell: (col) =>
      ({
        ...column.onHeaderCell?.(col),
        style: {
          ...column.onHeaderCell?.(col)?.style,
          position: 'relative',
          userSelect: 'none'
        },
        onMouseDown: (e: React.MouseEvent) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const isResizeArea = e.clientX > rect.right - 10
          if (isResizeArea && columnIndex < columns.length - 1) {
            handleResizeStart(e, columnKey)
          }
        },
        onMouseMove: (e: React.MouseEvent) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const isResizeArea = e.clientX > rect.right - 10
          if (isResizeArea && columnIndex < columns.length - 1) {
            ;(e.currentTarget as HTMLElement).style.cursor = 'col-resize'
          } else {
            ;(e.currentTarget as HTMLElement).style.cursor = 'pointer'
          }
        },
        onMouseLeave: (e: React.MouseEvent) => {
          ;(e.currentTarget as HTMLElement).style.cursor = 'default'
        }
      }) as any
  }

  if (column.render) {
    const originalRender = column.render
    columnWithSort.render = (value: any, record: T, index: number) => {
      const editingContext: EditingContext = {
        editingRowId: null,
        editingCell,
        editingCellData,
        hoveredRowId: null,
        onRowEdit: () => {},
        onRowSave: () => {},
        onRowCancel: () => {},
        onCellEdit: (record: any, columnKey: string) => {
          const cellValue = (record as any)[columnKey]
          handleCellEdit((record as any).id, columnKey, cellValue)
        },
        onCellSave: (record: any, columnKey: string) => handleCellSave((record as any).id, columnKey),
        onCellCancel: handleCellCancel,
        onCellDataChange: handleCellDataChange,
        onFieldEdit: handleFieldEdit,
        renderStandardEditCell: (record: any, columnKey: string) => {
          const cellColumn = columns.find((c) => c.key === columnKey) || column
          const cellValue = (record as any)[columnKey]
          return renderStandardEditCell(
            cellColumn,
            record,
            cellValue,
            handleCellDataChange,
            () => handleCellSave((record as any).id, columnKey),
            handleCellCancel
          )
        },
        renderStandardActionButtons: () => null
      }
      return originalRender(value, record, index, editingContext)
    }
  } else if (enableCellEditing && column.editable) {
    columnWithSort.render = (value: any, record: T, index: number) => {
      const cellKey = `${(record as any).id}-${columnKey}`
      const isEditing = editingCell === cellKey

      if (isEditing && enableCellEditing) {
        return renderStandardEditCell(
          column,
          record,
          editingCellData,
          handleCellDataChange,
          () => handleCellSave((record as any).id, columnKey),
          handleCellCancel
        )
      }

      const cellClickHandler =
        enableCellEditing && !isEditing ? () => handleCellEdit((record as any).id, columnKey, value) : undefined

      return (
        <div
          onClick={cellClickHandler}
          style={{
            cursor: enableCellEditing && !isEditing ? 'pointer' : 'default',
            width: '100%',
            height: '100%'
          }}
          className={enableCellEditing && !isEditing ? 'hover:bg-gray-100' : ''}
        >
          {value || ''}
        </div>
      )
    }
  }

  if (currentSortConfig && currentSortConfig.column === columnKey && currentSortConfig.direction) {
    columnWithSort.sortOrder = currentSortConfig.direction === 'asc' ? 'ascend' : 'descend'
  } else if (!currentSortConfig || !currentSortConfig.column) {
    if (columnKey === 'pipeline_stage' || columnKey === 'name' || columnKey === 'last_name') {
      columnWithSort.sortOrder = 'ascend'
    }
  }

  const actualFilterKey = column.filterKey || columnKey
  if (column.filterDropdown && currentFilters[actualFilterKey] !== undefined) {
    const filterValue = currentFilters[actualFilterKey]

    if (column.filterType === 'text' || (!column.filterType && column.filterDropdown)) {
      columnWithSort.filteredValue = filterValue ? [filterValue] : []
    } else if (column.filterType === 'multiselect') {
      columnWithSort.filteredValue = Array.isArray(filterValue) ? filterValue : []
    } else if (column.filterType === 'range') {
      columnWithSort.filteredValue =
        filterValue && (filterValue.min !== undefined || filterValue.max !== undefined) ? [filterValue] : []
    } else if (column.filterType === 'date_range') {
      columnWithSort.filteredValue =
        filterValue && (filterValue.min !== undefined || filterValue.max !== undefined) ? [filterValue] : []
    } else if (column.filterType === 'custom') {
      const hasMeaningfulContent =
        filterValue &&
        !(Array.isArray(filterValue) && filterValue.length === 0) &&
        !(typeof filterValue === 'object' && Object.keys(filterValue).length === 0)

      columnWithSort.filteredValue = hasMeaningfulContent ? [filterValue] : []
    }
  } else if (column.filterDropdown) {
    columnWithSort.filteredValue = []
  } else {
    columnWithSort.filteredValue = null
  }

  return columnWithSort
}
